import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Eye, 
  EyeSlash, 
  Code, 
  GraduationCap, 
  Gift,
  Phone,
  CalendarBlank,
  MapPin,
  Books,
  Student,
  ArrowRight,
  ArrowLeft
} from "@phosphor-icons/react";

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  
  // Step 1: Basic Info
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [inviteToken, setInviteToken] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // Step 2: Profile Details
  const [mobileNo, setMobileNo] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [homeAddress, setHomeAddress] = useState("");
  const [batchName, setBatchName] = useState("");
  const [courseName, setCourseName] = useState("");
  const [referralCode, setReferralCode] = useState("");
  
  const [loading, setLoading] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();

  // Get referral code from URL if present
  useEffect(() => {
    const refCode = searchParams.get('ref');
    const inviteTokenFromUrl = searchParams.get('invite');
    const inviteCodeFromUrl = searchParams.get('code');

    if (inviteTokenFromUrl) {
      setInviteToken(inviteTokenFromUrl);
      toast.info("Invite link detected");
    }
    if (inviteCodeFromUrl) {
      setInviteCode(inviteCodeFromUrl.toUpperCase());
    }
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      toast.info("Referral code applied!");
    }
  }, [searchParams]);

  if (user) {
    navigate("/", { replace: true });
    return null;
  }

  const validateStep1 = () => {
    if (!email || !password || !fullName) {
      toast.error("Please fill in all required fields");
      return false;
    }
    if (!inviteCode && !inviteToken) {
      toast.error("Invite code or invite link is required");
      return false;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep1()) return;

    setLoading(true);
    try {
      const registerResult = await register(
        email, 
        password, 
        fullName, 
        referralCode || null,
        inviteCode || null,
        inviteToken || null,
        mobileNo || null,
        dateOfBirth || null,
        homeAddress || null,
        batchName || null,
        courseName || null
      );

      if (registerResult?.verification_required) {
        const params = new URLSearchParams();
        params.set("email", email);
        navigate(`/verify-email?${params.toString()}`);
      } else {
        navigate("/");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string" ? detail : detail?.message;
      toast.error(message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1767477665589-3e729f5909f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxjeWJlcnB1bmslMjBhYnN0cmFjdCUyMGNvZGUlMjB0ZXh0dXJlfGVufDB8fHx8MTc3NDM3OTM5NXww&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-background/90 via-background/70 to-transparent" />
        <div className="relative z-10 flex flex-col justify-center px-12 py-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-lg bg-primary/20 border border-primary/50 flex items-center justify-center">
              <Code weight="bold" className="w-7 h-7 text-primary" />
            </div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">DIIT Showcase</h1>
          </div>
          <h2 className="font-heading text-4xl font-bold tracking-tight mb-4 leading-tight">
            Join the community.<br />
            <span className="text-accent neon-text">Start building.</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-md leading-relaxed">
            Create your portfolio, share code demos, upload media, and connect with fellow learners.
          </p>
          <div className="mt-6">
            <div className="flex items-center gap-3">
              <Button asChild variant="outline" className="rounded-full border-accent/50 text-accent hover:bg-accent/10">
                <Link to="/feed/public" data-testid="register-hero-public-feed-link">
                  Browse Public Feed
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-accent/50 text-accent hover:bg-accent/10">
                <Link to="/leaderboard/public" data-testid="register-hero-public-leaderboard-link">
                  View Public Leaderboard
                </Link>
              </Button>
            </div>
          </div>
          <div className="mt-12 space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <Code className="w-4 h-4 text-primary" />
              </div>
              <span>Live HTML/CSS/JS sandboxed previews</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-accent" />
              </div>
              <span>Get feedback from peers and teachers</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 rounded-full bg-warning/20 flex items-center justify-center">
                <Gift className="w-4 h-4 text-warning" />
              </div>
              <span>Earn rewards by referring friends</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Register Form */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-6 justify-center">
            <div className="w-10 h-10 rounded-lg bg-primary/20 border border-primary/50 flex items-center justify-center">
              <Code weight="bold" className="w-6 h-6 text-primary" />
            </div>
            <h1 className="font-heading text-xl font-bold">DIIT Showcase</h1>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step >= 1 ? 'bg-primary text-primary-foreground' : 'bg-surface border border-border'
              }`}>1</div>
              <span className="text-sm hidden sm:inline">Account</span>
            </div>
            <div className={`w-12 h-0.5 ${step >= 2 ? 'bg-primary' : 'bg-border'}`} />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step >= 2 ? 'bg-primary text-primary-foreground' : 'bg-surface border border-border'
              }`}>2</div>
              <span className="text-sm hidden sm:inline">Profile</span>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="font-heading text-2xl font-bold tracking-tight mb-2">
              {step === 1 ? "Create account" : "Complete your profile"}
            </h2>
            <p className="text-muted-foreground">
              {step === 1 
                ? "Join DIIT Showcase and start sharing your work" 
                : "Tell us more about yourself (optional but recommended)"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-sm font-medium">Full Name *</Label>
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="John Doe"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="h-12 bg-surface border-border focus:border-primary transition-colors"
                    data-testid="register-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@diit.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 bg-surface border-border focus:border-primary transition-colors"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inviteCode" className="text-sm font-medium">Invite Code *</Label>
                  <Input
                    id="inviteCode"
                    type="text"
                    placeholder="Enter invite code"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    className="h-12 bg-surface border-border focus:border-primary transition-colors uppercase tracking-wider font-mono"
                    data-testid="register-invite-input"
                  />
                  {inviteToken && (
                    <p className="text-xs text-accent">Invite token from link is active.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">Password *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Min 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 bg-surface border-border focus:border-primary transition-colors pr-12"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="rounded-lg border border-border/50 bg-surface/50 p-3 text-sm text-muted-foreground">
                  Accounts created via invite are student accounts. Teacher access is assigned by admin only.
                </div>

                <Button
                  type="button"
                  onClick={handleNext}
                  className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-full btn-neon mt-2"
                  data-testid="register-next-button"
                >
                  <span className="flex items-center gap-2">
                    Next Step
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="mobileNo" className="text-sm font-medium flex items-center gap-2">
                      <Phone className="w-4 h-4 text-primary" />
                      Mobile No
                    </Label>
                    <Input
                      id="mobileNo"
                      type="tel"
                      placeholder="+91 9876543210"
                      value={mobileNo}
                      onChange={(e) => setMobileNo(e.target.value)}
                      className="h-11 bg-surface border-border focus:border-primary transition-colors"
                      data-testid="register-mobile-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirth" className="text-sm font-medium flex items-center gap-2">
                      <CalendarBlank className="w-4 h-4 text-primary" />
                      Date of Birth
                    </Label>
                    <Input
                      id="dateOfBirth"
                      type="date"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                      className="h-11 bg-surface border-border focus:border-primary transition-colors"
                      data-testid="register-dob-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="homeAddress" className="text-sm font-medium flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    Home Address
                  </Label>
                  <Textarea
                    id="homeAddress"
                    placeholder="Enter your complete address"
                    value={homeAddress}
                    onChange={(e) => setHomeAddress(e.target.value)}
                    rows={2}
                    className="bg-surface border-border focus:border-primary transition-colors resize-none"
                    data-testid="register-address-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="batchName" className="text-sm font-medium flex items-center gap-2">
                      <Student className="w-4 h-4 text-primary" />
                      Batch Name
                    </Label>
                    <Input
                      id="batchName"
                      type="text"
                      placeholder="e.g., 2024-A"
                      value={batchName}
                      onChange={(e) => setBatchName(e.target.value)}
                      className="h-11 bg-surface border-border focus:border-primary transition-colors"
                      data-testid="register-batch-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="courseName" className="text-sm font-medium flex items-center gap-2">
                      <Books className="w-4 h-4 text-primary" />
                      Course Name
                    </Label>
                    <Input
                      id="courseName"
                      type="text"
                      placeholder="e.g., Web Development"
                      value={courseName}
                      onChange={(e) => setCourseName(e.target.value)}
                      className="h-11 bg-surface border-border focus:border-primary transition-colors"
                      data-testid="register-course-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="referralCode" className="text-sm font-medium flex items-center gap-2">
                    <Gift className="w-4 h-4 text-accent" />
                    Referral Code (Optional)
                  </Label>
                  <Input
                    id="referralCode"
                    type="text"
                    placeholder="Enter referral code"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    className="h-11 bg-surface border-border focus:border-primary transition-colors uppercase tracking-wider font-mono"
                    data-testid="register-referral-input"
                  />
                  {referralCode && (
                    <p className="text-xs text-accent">Referral code applied! Your referrer will earn points.</p>
                  )}
                </div>

                <div className="flex gap-3 mt-4">
                  <Button
                    type="button"
                    onClick={handleBack}
                    variant="outline"
                    className="flex-1 h-12 rounded-full"
                    data-testid="register-back-button"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-full btn-neon"
                    data-testid="register-submit-button"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                        Creating...
                      </span>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </div>
              </>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-primary hover:text-primary/80 font-medium transition-colors"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
            <p className="text-sm text-muted-foreground mt-3">
              Parent or visitor?{" "}
              <Link
                to="/feed/public"
                className="text-accent hover:text-accent/80 font-medium transition-colors"
                data-testid="register-public-leaderboard-link"
              >
                Open public feed
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
